<template>
  <div class="trip-report-page">
    <TitleBar v-if="!getMobileAppView">
      <template v-slot:title>
        <translate>Trip report</translate>
      </template>
      <template v-slot:buttons>
        <GekkoButton
          :to="{ name: 'Reports' }"
          :text="$gettext('Back to reports')"
          icon="fa-solid fa-arrow-left"
          variant="outline-primary"
          class="mr-2"
        />
        <GekkoButton
          @click="download"
          :text="$gettext('Download')"
          icon="fa-solid fa-download"
          variant="outline-primary"
        />
      </template>
    </TitleBar>

    <div class="content">
      <div class="trip-report-table-wrap">
        <FilterBar
          filter-bar-type="trips"
          :mainFilters="['DateRangeFilter', 'CarFilter']"
          :cookieName="'TripsReportFilterOptionsVue'"
          @change="onFilterBarChange"
        >
          <template v-if="tripsWithPlacement.length" v-slot:right-bar>
            <GekkoButton
              v-if="getMobileAppView"
              @click="download"
              :text="$gettext('Download')"
              icon="fa-solid fa-download"
              variant="outline-primary"
            />
            <TripReportTotals
              :distance="formatNumber(totalDistance)"
              :excluded="tripsExcludedCount"
              :gap-km="formatNumber(totalGapDistance)"
            />
          </template>
        </FilterBar>
        <div v-if="hasOdometerOverlaps" class="trip-report-page__overlap-warning">
          <strong>{{ $gettext('Overlap check') }}:</strong>
          {{ overlapSummaryText }}
        </div>
        <div v-if="odometerGaps.length" class="trip-report-page__gap-warning">
          <strong>{{ $gettext('Gap check') }}:</strong>
          {{ gapSummaryText }}
        </div>
        <div class="content--inner">
          <template v-if="loading">
            <div class="trip-report-page__loading">
              <LoadingSpinner min-height />
            </div>
          </template>
          <template v-else-if="!carSelected">
            <div class="trip-report-page__empty">
              {{ $gettext('Select a car to view the report.') }}
            </div>
          </template>
          <template v-else-if="!tripsWithPlacement.length">
            <div class="trip-report-page__empty">
              {{ $gettext('No trips found for the selected period.') }}
            </div>
          </template>
          <div v-else class="trip-report-chart-wrap">
            <div class="trip-report-chart" :style="{ '--row-height': rowHeight + 'px' }" :class="{ 'is-compact': rowHeight < 70, 'is-dense': rowHeight <= 28 }">
              <div class="trip-report-chart__zoom-controls">
                <button
                  type="button"
                  class="trip-report-chart__zoom-btn trip-report-chart__zoom-btn--out"
                  :title="$gettext('Zoom out')"
                  :disabled="!canZoomOut"
                  @click.stop="zoomOut"
                >
                  -
                </button>
                <button
                  type="button"
                  class="trip-report-chart__zoom-btn trip-report-chart__zoom-btn--reset"
                  :title="$gettext('Reset zoom')"
                  @click.stop="resetZoom"
                >
                  {{ zoomLabel }}
                </button>
                <button
                  type="button"
                  class="trip-report-chart__zoom-btn trip-report-chart__zoom-btn--in"
                  :title="$gettext('Zoom in')"
                  :disabled="!canZoomIn"
                  @click.stop="zoomIn"
                >
                  +
                </button>
              </div>

              <div class="trip-report-chart__density-controls">
                <button
                  type="button"
                  class="trip-report-chart__density-btn"
                  :class="{ 'is-active': rowHeight === 50 }"
                  :title="$gettext('Normal rows')"
                  @click.stop="setRowHeight(50)"
                ><font-awesome-icon aria-hidden="true" icon="fa-solid fa-table-list" /></button>
                <button
                  type="button"
                  class="trip-report-chart__density-btn"
                  :class="{ 'is-active': rowHeight === 28 }"
                  :title="$gettext('Compact rows')"
                  @click.stop="setRowHeight(28)"
                ><font-awesome-icon aria-hidden="true" icon="fa-solid fa-bars" /></button>
              </div>

              <div class="trip-report-chart__summary-column">
                <div class="trip-report-chart__meta-head">
                  {{ $gettext('Trip / Odometer') }}
                </div>

                <div class="trip-report-chart__summary-rows">
                  <button
                    v-for="trip in tripsWithPlacement"
                    :key="trip.id"
                    type="button"
                    class="trip-report-chart__meta trip-report-chart__meta--button"
                    :class="{ 'is-hovered': hoveredTripId === trip.id, 'is-overlap': isTripOverlapping(trip) }"
                    @click="openTripOdometerModal(trip)"
                    @mouseenter="hoveredTripId = trip.id"
                    @mouseleave="hoveredTripId = null"
                    :aria-label="$gettext('Edit odometer for this trip')"
                  >
                    <div class="trip-report-chart__meta-title">
                      <div class="trip-report-chart__meta-date">{{ tripDate(trip.date) }}</div>
                      <div class="trip-report-chart__meta-route">
                        <font-awesome-icon
                          v-if="isTripOverlapping(trip)"
                          aria-hidden="true"
                          icon="fa-solid fa-triangle-exclamation"
                          class="trip-report-chart__meta-warning-icon"
                          :title="$gettext('Overlapping odometer range')"
                        />
                        {{ trip.start.name }} &rarr; {{ trip.end.name }}
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <overlay-scrollbars
                ref="tripsOverlayScroll"
                class="trip-report-chart__timeline-scroll"
                :options="scrollBarOptions"
              >
                <div ref="tripsTimeline" class="trip-report-chart__timeline">
                  <div class="trip-report-chart__odometer-head" :style="odometerTimelineStyle">
                    <div
                      v-for="tick in odometerTicks"
                      :key="tick"
                      class="trip-report-chart__odometer-tick"
                      :style="odometerTickStyle(tick)"
                    >
                      <span class="trip-report-chart__odometer-label">{{ tick }}</span>
                    </div>
                  </div>

                  <div class="trip-report-chart__timeline-rows">
                    <div
                      v-for="trip in tripsWithPlacement"
                      :key="trip.id"
                      class="trip-report-chart__line"
                      :style="odometerTimelineStyle"
                      @mouseenter="hoveredTripId = trip.id"
                      @mouseleave="hoveredTripId = null"
                    >
                      <div class="trip-report-chart__line-label" :style="tripLineLabelStyle(trip)">
                        <font-awesome-icon
                          v-if="isTripOverlapping(trip)"
                          aria-hidden="true"
                          icon="fa-solid fa-triangle-exclamation"
                          class="trip-report-chart__meta-warning-icon"
                        />
                        {{ tripDate(trip.date) }} &bull; {{ trip.start.name.slice(0, 3).toUpperCase() }} &rarr; {{ trip.end.name.slice(0, 3).toUpperCase() }}
                      </div>
                      <button
                        type="button"
                        class="trip-report-chart__bar"
                        :class="[
                          transportClass(trip.transport_type),
                          { 'is-overlap': isTripOverlapping(trip), 'is-hovered': hoveredTripId === trip.id },
                        ]"
                        :style="tripOdometerBarStyle(trip)"
                        :title="tripTooltip(trip)"
                        @click="openTripOdometerModal(trip)"
                      >
                        {{ Number(trip.total_distance).toFixed(1) }} km
                      </button>
                    </div>
                    <div
                      v-for="(gap, gapIdx) in odometerGaps"
                      :key="'gap_' + gapIdx"
                      class="trip-report-chart__gap-band"
                      :style="gapBandStyle(gap)"
                      :title="$gettextInterpolate($gettext('Odometer gap: %{km}s km'), { km: formatNumber(gap.end - gap.start) })"
                    />
                    <div
                      v-for="(range, rangeIdx) in odometerOverlapRanges"
                      :key="'overlap_' + rangeIdx"
                      class="trip-report-chart__overlap-band"
                      :style="overlapBandStyle(range)"
                      :title="$gettextInterpolate($gettext('Overlapping odometer range: %{km}s km'), { km: formatNumber(range.end - range.start) })"
                    />
                  </div>
                </div>
              </overlay-scrollbars>
            </div>
          </div>

          <TripOdometerModal
            :show-modal="showOdometerModal"
            :trip="selectedTrip"
            @close="closeTripOdometerModal"
            @saved="onTripOdometerSaved"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'
import TripService from '@/store/services/trip.service'
import { mapGetters } from 'vuex'
import FilterBar from '@/components/list_filters/FilterBar'
import TripOdometerModal from '@/components/trips/TripOdometerModal'
import TripReportTotals from '@/components/trips/TripReportTotals'

const TARGET_MEDIAN_BAR_PX = 120 // target px width for the median trip bar at 100% zoom
const ADAPTIVE_PX_PER_KM_MIN = 0.05 // prevents absurdly wide timelines for very long trips
const ADAPTIVE_PX_PER_KM_MAX = 5 // prevents absurdly compressed timelines for very short trips
const STANDARD_ZOOM_PERCENT_LEVELS = [25, 33, 50, 67, 75, 90, 100, 110, 125, 150, 175, 200, 250, 300, 400]
const DEFAULT_ZOOM_PERCENT = 100
const DEFAULT_ZOOM_LEVEL_INDEX = STANDARD_ZOOM_PERCENT_LEVELS.indexOf(DEFAULT_ZOOM_PERCENT)
const MIN_TICK_SPACING_PX = 90
const ODOMETER_TICK_CANDIDATES = [100, 200, 500, 1000, 2000, 5000, 10000]
const ODOMETER_SCROLL_EDGE_PADDING_PX = 50 // extra space at both left and right edges of the timeline
const ODOMETER_OVERLAP_EPSILON = 0.000001
const WHEEL_ZOOM_THROTTLE_MS = 120
const OVERLAP_BAR_COLOR = '#c0313e'

function isFiniteOdometerNumber(value) {
  return Number.isFinite(Number(value))
}

function getTripRange(trip) {
  const end = Number(trip.end_odometer)
  const distance = Number(trip.total_distance)
  const start = end - distance
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return null
  }
  return {
    start: Math.min(start, end),
    end: Math.max(start, end),
  }
}

function getTripsWithOdometer(trips) {
  return trips
    .filter(
      (trip) =>
        trip.transport_type === 'car' &&
        isFiniteOdometerNumber(trip.end_odometer) &&
        isFiniteOdometerNumber(trip.total_distance)
    )
    .sort((a, b) => {
      const dateDiff = new Date(a.date) - new Date(b.date)
      if (dateDiff !== 0) return dateDiff
      return Number(a.end_odometer) - Number(b.end_odometer)
    })
}

function getOdometerOverlapPairs(tripsWithPlacement) {
  const pairs = []
  const ranges = tripsWithPlacement
    .map((trip) => {
      const range = getTripRange(trip)
      return range
        ? {
            trip,
            start: range.start,
            end: range.end,
          }
        : null
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start)

  for (let i = 0; i < ranges.length; i += 1) {
    for (let j = i + 1; j < ranges.length; j += 1) {
      // Since ranges are sorted by start, no later range can overlap after this point.
      if (ranges[j].start >= ranges[i].end - ODOMETER_OVERLAP_EPSILON) {
        break
      }

      const intersectionStart = Math.max(ranges[i].start, ranges[j].start)
      const intersectionEnd = Math.min(ranges[i].end, ranges[j].end)
      const intersectionLength = intersectionEnd - intersectionStart

      // Touching boundaries (end == start) are not overlaps.
      if (intersectionLength > ODOMETER_OVERLAP_EPSILON) {
        pairs.push([ranges[i].trip.id, ranges[j].trip.id])
      }
    }
  }

  return pairs
}

export default {
  name: 'TripsReport',
  components: {
    TitleBar,
    GekkoButton,
    LoadingSpinner,
    FilterBar,
    TripOdometerModal,
    TripReportTotals,
  },
  data() {
    const VALID_ROW_HEIGHTS = [50, 28]
    let rowHeight = 50
    try {
      const stored = Number(localStorage.getItem('tripsReportRowHeight'))
      if (VALID_ROW_HEIGHTS.includes(stored)) rowHeight = stored
    } catch (_) { console.log('Error reading local storage') }
    return {
      loading: false,
      trips: [],
      carSelected: false,
      isDragScrollBound: false,
      didScroll: false,
      lastWheelZoomAt: 0,
      rowHeight,
      hoveredTripId: null,
      zoomLevelIndex: DEFAULT_ZOOM_LEVEL_INDEX === -1 ? 0 : DEFAULT_ZOOM_LEVEL_INDEX,
      showOdometerModal: false,
      selectedTrip: null,
      scrollBarOptions: {
        scrollbars: {
          visibility: 'auto',
          autoHide: 'never',
          autoHideDelay: 800,
          dragScrolling: true,
          clickScrolling: true,
          touchSupport: true,
          snapHandle: true,
        },
        overflowBehavior: { y: 'scroll', x: 'scroll' },
      },
    }
  },
  watch: {
    loading() {
      if (!this.loading) {
        this.attachDraggableListeners()
      }
    },
  },
  computed: {
    ...mapGetters({
      getCompanyId: 'main/getCompanyId',
      getFilter: 'filterStore/getFilter',
      getPreferredLanguage: 'main/getPreferredLanguage',
      getMobileAppView: 'main/getMobileAppView',
    }),
    tripRanges() {
      return this.tripsWithPlacement.map(getTripRange).filter(Boolean)
    },
    odometerMin() {
      if (!this.tripRanges.length) return 0
      const min = Math.min(...this.tripRanges.map((r) => r.start))
      return Math.floor(min / this.odometerTickStep) * this.odometerTickStep
    },
    odometerMax() {
      if (!this.tripRanges.length) return 1000
      const max = Math.max(...this.tripRanges.map((r) => r.end))
      return Math.ceil(max / this.odometerTickStep) * this.odometerTickStep
    },
    odometerTickStep() {
      const pxPerKm = this.odometerPxPerKm
      return (
        ODOMETER_TICK_CANDIDATES.find((step) => step * pxPerKm >= MIN_TICK_SPACING_PX) ||
        ODOMETER_TICK_CANDIDATES[ODOMETER_TICK_CANDIDATES.length - 1]
      )
    },
    odometerTicks() {
      // Array of tick values with spacing adjusted to current zoom level
      const ticks = []
      for (let km = this.odometerMin; km <= this.odometerMax; km += this.odometerTickStep) {
        ticks.push(km)
      }
      return ticks
    },
    odometerTimelineStyle() {
      // Calculate the dynamic width, but never less than 100% of the container
      const pxWidth = (this.odometerMax - this.odometerMin) * this.odometerPxPerKm
      return {
        width: `max(${pxWidth + ODOMETER_SCROLL_EDGE_PADDING_PX * 2}px, 100%)`,
        '--odometer-px': `${this.odometerPxPerKm}px`,
      }
    },
    currentZoomPercent() {
      return STANDARD_ZOOM_PERCENT_LEVELS[this.zoomLevelIndex]
    },
    adaptiveBasePxPerKm() {
      const distances = this.tripsWithPlacement
        .map((t) => Number(t.total_distance))
        .filter((d) => Number.isFinite(d) && d > 0)
        .sort((a, b) => a - b)
      if (!distances.length) return 0.2
      // Use P25 (lower quartile): 75% of bars will be at or above target width,
      // giving better overall readability than the median (P50) which leaves half too small.
      const p25Idx = Math.floor(distances.length * 0.25)
      const p25 = distances[p25Idx]
      return Math.min(ADAPTIVE_PX_PER_KM_MAX, Math.max(ADAPTIVE_PX_PER_KM_MIN, TARGET_MEDIAN_BAR_PX / p25))
    },
    odometerPxPerKm() {
      return this.adaptiveBasePxPerKm * (this.currentZoomPercent / 100)
    },
    odometerGaps() {
      const ranges = this.tripsWithPlacement
        .map((trip) => {
          const range = getTripRange(trip)
          return range ? { start: range.start, end: range.end } : null
        })
        .filter(Boolean)
        .sort((a, b) => a.start - b.start)
      const gaps = []
      for (let i = 0; i < ranges.length - 1; i += 1) {
        if (ranges[i + 1].start > ranges[i].end + ODOMETER_OVERLAP_EPSILON) {
          gaps.push({ start: ranges[i].end, end: ranges[i + 1].start })
        }
      }
      return gaps
    },
    gapSummaryText() {
      let text = this.$gettext('%{count}s odometer gap(s) detected.')
      return this.$gettextInterpolate(text, { count: this.odometerGaps.length })
    },
    zoomLabel() {
      return `${this.currentZoomPercent}%`
    },
    canZoomIn() {
      return this.zoomLevelIndex < STANDARD_ZOOM_PERCENT_LEVELS.length - 1
    },
    canZoomOut() {
      return this.zoomLevelIndex > 0
    },
    tripsWithPlacement() {
      // Only show trips with odometer data, ordered by date.
      return getTripsWithOdometer(this.trips)
    },
    odometerOverlapRanges() {
      // Build a map of tripId → range for quick lookup
      const rangeById = {}
      this.tripsWithPlacement.forEach((trip) => {
        const r = getTripRange(trip)
        if (r) rangeById[trip.id] = r
      })
      // Collect raw intersection intervals from each pair
      const intervals = []
      this.odometerOverlapPairs.forEach(([idA, idB]) => {
        const a = rangeById[idA]
        const b = rangeById[idB]
        if (!a || !b) return
        const start = Math.max(a.start, b.start)
        const end = Math.min(a.end, b.end)
        if (end > start + ODOMETER_OVERLAP_EPSILON) intervals.push({ start, end })
      })
      if (!intervals.length) return []
      // Merge overlapping intervals so bands don't stack
      intervals.sort((a, b) => a.start - b.start)
      const merged = [{ ...intervals[0] }]
      for (let i = 1; i < intervals.length; i++) {
        const last = merged[merged.length - 1]
        if (intervals[i].start <= last.end) {
          last.end = Math.max(last.end, intervals[i].end)
        } else {
          merged.push({ ...intervals[i] })
        }
      }
      return merged
    },
    odometerOverlapPairs() {
      return getOdometerOverlapPairs(this.tripsWithPlacement)
    },
    overlappingTripIds() {
      // Return as array (not Set) — Vue 2 cannot observe Set mutations so
      // template expressions using .has() on a returned Set don't reliably
      // re-evaluate when the computed is invalidated.
      return [...new Set(this.odometerOverlapPairs.flat())]
    },
    hasOdometerOverlaps() {
      return this.odometerOverlapPairs.length > 0
    },
    overlapSummaryText() {
      let text = this.$gettext('%{pairs}s overlapping range(s) found across %{trips}s trip(s).')
      text = this.$gettextInterpolate(text, {
        pairs: this.odometerOverlapPairs.length,
        trips: this.overlappingTripIds.length,
      })
      return text
    },
    tripsExcludedCount() {
      const shown = new Set(this.tripsWithPlacement.map((t) => t.id))
      return this.trips.filter((t) => t.transport_type === 'car' && !shown.has(t.id)).length
    },
    totalDistance() {
      return this.tripsWithPlacement.reduce((sum, t) => sum + (Number(t.total_distance) || 0), 0)
    },
    totalGapDistance() {
      return this.odometerGaps.reduce((sum, g) => sum + (g.end - g.start), 0)
    },
  },
  mounted() {
    this.loadTrips()
  },
  methods: {
    async loadTrips(filterBarFilters = {}) {
      if (this.loading) {
        return
      }

      // Build API filters from store or FilterBar state
      const filters = this.buildApiFilters(filterBarFilters)

      // Only request trips if a car id is provided
      if (!filters.car) {
        this.trips = []
        this.carSelected = false
        this.loading = false
        return
      }

      this.carSelected = true
      this.loading = true
      this.isDragScrollBound = false
      this.zoomLevelIndex = DEFAULT_ZOOM_LEVEL_INDEX === -1 ? 0 : DEFAULT_ZOOM_LEVEL_INDEX

      try {
        const response = await TripService.list(filters)
        if (Array.isArray(response.data)) {
          this.trips = response.data
        } else if (response.data && Array.isArray(response.data.results)) {
          this.trips = response.data.results
        } else {
          this.trips = []
        }
      } catch (error) {
        this.trips = []
      } finally {
        this.loading = false
      }
    },
    attachDraggableListeners() {
      this.$nextTick(() => {
        const slider = this.$refs.tripsTimeline
        const overlayScroll = this.$refs.tripsOverlayScroll

        if (!slider || !overlayScroll || this.isDragScrollBound) {
          return
        }

        this.isDragScrollBound = true

        let isDown = false
        let startX = 0
        let scrollLeft = 0

        slider.addEventListener('mousedown', (event) => {
          this.didScroll = false
          isDown = true
          startX = event.pageX - slider.offsetLeft
          scrollLeft = overlayScroll.osInstance().scroll().position.x
        })

        slider.addEventListener('mouseleave', () => {
          isDown = false
        })

        slider.addEventListener('mouseup', () => {
          isDown = false
        })

        slider.addEventListener('mousemove', (event) => {
          if (!isDown) return

          this.didScroll = true
          event.preventDefault()

          const currentX = event.pageX - slider.offsetLeft
          const walk = currentX - startX
          overlayScroll.osInstance().scroll({ x: scrollLeft - walk }, 0, 'linear')
        })
      })
    },
    buildApiFilters(filterBarFilters = {}) {
      const { DateRangeFilter: dr = {}, TransportTypeFilter: tt = {}, CarFilter: cf = {} } = filterBarFilters
      const store = this.getFilter || {}
      const pick = (...vals) => vals.find(Boolean) || undefined

      return {
        company_profile: this.getCompanyId,
        extended: 'True',
        ...(pick(dr.fromDate, store.start_date) && { start_date: pick(dr.fromDate, store.start_date) }),
        ...(pick(dr.toDate, store.end_date) && { end_date: pick(dr.toDate, store.end_date) }),
        ...(pick(tt.key, store.transport_type) && { transport_type: pick(tt.key, store.transport_type) }),
        ...(pick(tt.car, cf.key, store.car) && { car: pick(tt.car, cf.key, store.car) }),
      }
    },
    onFilterBarChange(filterState) {
      // filterState contains DateRangeFilter, TransportTypeFilter, and CarFilter values from FilterBar
      this.loadTrips(filterState)
    },
    // TODO: re-enable by adding @wheel.native="onChartWheel" to the overlay-scrollbars element
    onChartWheel(event) {
      if (!event.ctrlKey) {
        return
      }
      event.preventDefault()
      const now = Date.now()
      if (now - this.lastWheelZoomAt < WHEEL_ZOOM_THROTTLE_MS) {
        return
      }

      if (event.deltaY < 0) {
        this.zoomIn()
        this.lastWheelZoomAt = now
      } else if (event.deltaY > 0) {
        this.zoomOut()
        this.lastWheelZoomAt = now
      }
    },
    zoomIn() {
      if (!this.canZoomIn) {
        return
      }
      this.zoomLevelIndex += 1
    },
    zoomOut() {
      if (!this.canZoomOut) {
        return
      }
      this.zoomLevelIndex -= 1
    },

    resetZoom() {
      this.zoomLevelIndex = DEFAULT_ZOOM_LEVEL_INDEX === -1 ? 0 : DEFAULT_ZOOM_LEVEL_INDEX
    },
    setRowHeight(height) {
      this.rowHeight = height
      this.zoomLevelIndex = DEFAULT_ZOOM_LEVEL_INDEX === -1 ? 0 : DEFAULT_ZOOM_LEVEL_INDEX
      try { localStorage.setItem('tripsReportRowHeight', String(height)) } catch (_) { console.log('Error reading local storage') }
    },
    openTripOdometerModal(trip) {
      if (this.didScroll) {
        this.didScroll = false
        return
      }

      this.selectedTrip = trip
      this.showOdometerModal = true
    },
    closeTripOdometerModal() {
      this.showOdometerModal = false
      this.selectedTrip = null
    },
    onTripOdometerSaved(updatedTrip) {
      // Replace the array reference entirely so Vue 2 reliably recomputes all
      // dependents (tripsWithPlacement → odometerOverlapPairs → overlappingTripIds).
      // $set / splice-based mutation does not always invalidate element-level reads.
      this.trips = this.trips.map((trip) =>
        trip.id === updatedTrip.id ? { ...trip, ...updatedTrip } : trip
      )

      this.closeTripOdometerModal()
    },
    download() {
      let filters = this.$store.getters['filterStore/getFilter']
      filters['company_profile'] = this.getCompanyId
      TripService.exportTrips(filters)
        .then(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Trips Download'),
            text: this.$gettext('Your download is being prepared, check your downloads page to download the file.'),
            variant: 'info',
            duration: 4,
          })
          this.$router.push({ name: 'Files' })
        })
        .catch((error) => {
          if (error.status === 429) {
            const alert_text = this.$gettext("You've reached your download limit for today.")
            this.$store.commit('setAlert', { title: this.$gettext('Trips Download'), text: alert_text, variant: 'danger', duration: 4 })
            this.$store.commit('togglePremiumModal', 'web_files_download_limit')
          } else {
            this.$store.commit('setAlert', {
              title: this.$gettext('Trips Download Error'),
              text: this.$gettext('An error occurred while attempting to download your trips.'),
              variant: 'danger',
              duration: 4,
            })
          }
        })
    },
    tripDate(dateValue) {
      const lang = this.getPreferredLanguage ?? 'en'
      return moment(String(dateValue)).locale(lang).format('DD MMM YYYY')
    },
    formatNumber(value) {
      const parsed = Number(value)
      if (!Number.isFinite(parsed)) {
        return '-'
      }

      const rounded = Math.round(parsed * 10) / 10
      const hasDecimal = rounded % 1 !== 0
      return rounded.toLocaleString(undefined, {
        minimumFractionDigits: hasDecimal ? 1 : 0,
        maximumFractionDigits: 1,
      })
    },
    tripLabel(trip) {
      const number = trip.number ? `#${trip.number}` : `#${trip.id}`
      return `${number} - ${trip.start.name} -> ${trip.end.name}`
    },
    odometerText(trip) {
      const end = Number(trip.end_odometer)
      const distance = Number(trip.total_distance) || 0
      const start = end - distance

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return this.$gettext('No odometer')
      }

      return `${this.formatNumber(start)} → ${this.formatNumber(end)} km`
    },
    tripTooltip(trip) {
      const overlapSuffix = this.isTripOverlapping(trip)
        ? ` | ${this.$gettext('Overlaps with another trip range')}`
        : ''
      return `${this.tripDate(trip.date)} | ${this.odometerText(trip)}${overlapSuffix}`
    },
    tripDistanceText(trip) {
      const distance = Number(trip.total_distance)
      if (!Number.isFinite(distance)) {
        return this.$gettext('Unknown distance')
      }

      return this.$gettextInterpolate(this.$gettext('%{distance} km'), {
        distance: this.formatNumber(distance),
      })
    },
    transportClass(transportType) {
      if (transportType === 'car') return 'is-car'
      if (transportType === 'train') return 'is-train'
      if (transportType === 'bike') return 'is-bike'
      return 'is-other'
    },
    isTripOverlapping(trip) {
      return this.overlappingTripIds.includes(trip.id)
    },
    tripLineLabelStyle(trip) {
      const start = Number(trip.end_odometer) - Number(trip.total_distance)
      const end = Number(trip.end_odometer)
      const min = this.odometerMin
      return {
        left: `${ODOMETER_SCROLL_EDGE_PADDING_PX + (start - min) * this.odometerPxPerKm}px`,
        width: `${Math.max((end - start) * this.odometerPxPerKm, 60)}px`,
      }
    },
    tripOdometerBarStyle(trip) {
      // Calculate left and width based on odometer
      const start = Number(trip.end_odometer) - Number(trip.total_distance)
      const end = Number(trip.end_odometer)
      const min = this.odometerMin
      const style = {
        left: `${ODOMETER_SCROLL_EDGE_PADDING_PX + (start - min) * this.odometerPxPerKm}px`,
        width: `${Math.max((end - start) * this.odometerPxPerKm, 2)}px`,
      }

      if (this.isTripOverlapping(trip)) {
        style.background = OVERLAP_BAR_COLOR
      }

      return style
    },
    odometerTickStyle(tick) {
      return {
        left: `${ODOMETER_SCROLL_EDGE_PADDING_PX + (tick - this.odometerMin) * this.odometerPxPerKm}px`,
      }
    },
    gapBandStyle(gap) {
      return {
        left: `${ODOMETER_SCROLL_EDGE_PADDING_PX + (gap.start - this.odometerMin) * this.odometerPxPerKm}px`,
        width: `${Math.max((gap.end - gap.start) * this.odometerPxPerKm, 2)}px`,
      }
    },
    overlapBandStyle(range) {
      return {
        left: `${ODOMETER_SCROLL_EDGE_PADDING_PX + (range.start - this.odometerMin) * this.odometerPxPerKm}px`,
        width: `${Math.max((range.end - range.start) * this.odometerPxPerKm, 2)}px`,
      }
    },
  },
}
</script>

<style lang="scss">
// Mobile webview: flex chain so the timeline fills all remaining viewport height
// after the filterbar and any warning banners — no hardcoded pixel offsets needed.
.mobile-view .trip-report-page {
  display: flex;
  flex-direction: column;
  height: 100dvh;
  overflow: hidden;

  > .content,
  .trip-report-table-wrap,
  .content--inner,
  .trip-report-chart-wrap {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .trip-report-chart {
    flex: 1;
    min-height: 0;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
  }

  .trip-report-chart__summary-column {
    display: none;
  }

  .trip-report-chart__timeline-scroll {
    height: 100% !important;
  }
}
</style>

<style scoped lang="scss">
$chart-bg: #f5f7fb;

.trip-report-page {
  &__year-select {
    min-width: 110px;
  }

  &__loading,
  &__empty {
    min-height: 280px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__overlap-warning {
    margin-bottom: 0.5rem;
    border: 1px solid #f0b8bc;
    background: #fdf2f2;
    color: #7a1c24;
    border-radius: 0.4rem;
    padding: 0.55rem 0.7rem;
    font-size: 0.86rem;
  }

  &__gap-warning {
    margin-bottom: 0.5rem;
    border: 1px solid #b8d4e8;
    background: #eef6fc;
    color: #1a4a6e;
    border-radius: 0.4rem;
    padding: 0.55rem 0.7rem;
    font-size: 0.86rem;
  }
}

.trip-report-chart {
  border: 1px solid #e7ebf0;
  border-radius: 0.5rem;
  background: #ffffff;
  position: relative;
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  background: $chart-bg;
  &__summary-column {
    border-right: 1px solid #eef1f4;
    background: #ffffff;
  }

  &__timeline-scroll {
    min-width: 0;
  }

  &__timeline {
    min-width: 100%;
  }

  &__meta-head,
  &__meta {
    background: #ffffff;
    box-sizing: border-box;
  }

  &__meta-head {
    padding: 0.75rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    height: 38px;
    border-bottom: 1px solid #eef1f4;
  }

  &__odometer-head {
    position: relative;
    border-bottom: 1px solid #eef1f4;
    height: 38px;
    background: $chart-bg;
  }

  &__zoom-controls {
    position: absolute;
    top: 5px;
    right: 8px;
    z-index: 2;
    width: 132px;
    height: 28px;
  }

  &__zoom-btn {
    position: absolute;
    top: 0;
    border: 1px solid #d1dbe6;
    background: #ffffff;
    color: #2b3a4a;
    border-radius: 4px;
    min-width: 30px;
    height: 28px;
    font-size: 0.8rem;
    line-height: 1;
    padding: 0 0.45rem;

    &:hover:not(:disabled) {
      background: #edf3fa;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__zoom-btn--out {
    right: 102px;
    width: 30px;
  }

  &__zoom-btn--reset {
    right: 34px;
    width: 64px;
  }

  &__zoom-btn--in {
    right: 0;
    width: 30px;
  }

  &__density-controls {
    position: absolute;
    top: 5px;
    right: 148px;
    z-index: 2;
    display: flex;
    gap: 4px;
    height: 28px;
  }

  &__density-btn {
    border: 1px solid #d1dbe6;
    background: #ffffff;
    color: #2b3a4a;
    border-radius: 4px;
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #edf3fa;
    }

    &.is-active {
      background: #e0ecf8;
      border-color: #a0bcd8;
      font-weight: 600;
    }
  }

  &__odometer-tick {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: #bfc9d6;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  &__odometer-label {
    position: absolute;
    top: 18px;
    left: -18px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #2b3a4a;
    background: $chart-bg;
    padding: 0 2px;
    pointer-events: none;
  }

  &__summary-rows {
    .trip-report-chart__meta:not(:last-child) {
      border-bottom: 1px solid #f1f3f6;
    }
  }

  &__timeline-rows {
    position: relative;
    .trip-report-chart__line:not(:last-child) {
      border-bottom: 1px solid #f1f3f6;
    }
  }

  &__gap-band {
    position: absolute;
    top: 0;
    height: 100%;
    background: repeating-linear-gradient(
      45deg,
      rgba(180, 195, 210, 0.18) 0px,
      rgba(180, 195, 210, 0.18) 4px,
      transparent 4px,
      transparent 10px
    );
    border-left: 2px dashed #a0b4c8;
    border-right: 2px dashed #a0b4c8;
    pointer-events: none;
    z-index: 2;
  }

  &__overlap-band {
    position: absolute;
    top: 0;
    height: 100%;
    background: repeating-linear-gradient(
      45deg,
      rgba(200, 80, 0, 0.13) 0px,
      rgba(200, 80, 0, 0.13) 4px,
      transparent 4px,
      transparent 10px
    );
    border-left: 2px dashed #c85000;
    border-right: 2px dashed #c85000;
    pointer-events: none;
    z-index: 2;
  }

  &__line-label {
    display: none;
    pointer-events: none;
    position: absolute;
    top: 5px;
    z-index: 1;
    overflow: visible;
    font-size: 0.72rem;
    color: #2b3a4a;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    align-items: center;
    gap: 0.3rem;
  }

  &__meta-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.05rem;
    overflow: hidden;
    width: 100%;
  }

  &__meta-date {
    font-size: 0.75rem;
    font-weight: 400;
    color: #6f7b8a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    line-height: 1.2;

    .trip-report-chart.is-dense & {
      display: none;
    }
  }

  &__meta-route {
    font-size: 0.9rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.3rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    line-height: 1.2;
  }

  &__meta-warning-icon {
    color: #a64200;
    flex: 0 0 auto;
    font-size: 0.82rem;
  }

  &__meta-subtitle {
    font-size: 0.8rem;
    color: #6f7b8a;
    margin-top: 0.15rem;

    .trip-report-chart.is-compact & {
      margin-top: 0;
    }

    .trip-report-chart.is-dense & {
      display: none;
    }
  }

  &__meta {
    height: var(--row-height, 70px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    transition: height 0.15s ease;
  }
  &__meta--button {
    width: 100%;
    border: none;
    background: none;
    padding: 0 0.75rem;
    margin: 0;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
    &.is-overlap {
      background: #fdf2f2;
    }
    &:hover,
    &:focus,
    &.is-hovered {
      background: #f7fafd;
    }
  }

  &__line {
    position: relative;
    height: var(--row-height, 70px);
    width: 100%;
    box-sizing: border-box;
    background: repeating-linear-gradient(to right, #f7fafd 0, #f7fafd 99px, #eff3f7 100px, #f7fafd 101px);
    transition: height 0.15s ease;
  }

  &__bar {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: 30px;

    .trip-report-chart.is-compact & {
      height: 25px;
    }

    .trip-report-chart.is-dense & {
      height: 20px;
    }
    border: 0;
    border-radius: var(--gk-border-radius, 8px);
    color: #ffffff;
    font-size: 0.72rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 0.45rem;
    text-align: left;
    cursor: pointer;

    &.is-car {
      background: $colorBrand;
    }

    &.is-train {
      background: #2e7fa8;
    }

    &.is-bike {
      background: #4f8e46;
    }

    &.is-other {
      background: #7a8696;
    }

    &.is-overlap {
      box-shadow: 0 0 0 2px #c0313e inset;
    }

    &.is-hovered {
      filter: brightness(1.15);
      outline: 2px solid rgba(0, 0, 0, 0.18);
      outline-offset: 1px;
    }
  }
}

@media (max-width: 992px) {
  .trip-report-chart {
    grid-template-columns: 240px minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .trip-report-chart {
    grid-template-columns: 1fr;

    &__summary-column {
      display: none;
    }

    &__timeline-scroll {
      height: 60vh;
    }

    &__line {
      height: 60px;
    }

    &.is-dense &__line {
      height: 45px;
    }

    &__line-label {
      display: flex;
      top: 0;
    }

    &__bar {
      top: auto;
      bottom: 5px;
      transform: none;
    }

    &.is-dense &__bar {
      bottom: 5px;
    }
  }
}
</style>